import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CAlert,
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CCol,
  CForm,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CPasswordInput,
  CRow,
} from "@coreui/react-pro";
import config from "../config/config";
import { normalizeRole } from "../utils/roleMap";
import PublicAuthLayout from "../components/public/PublicAuthLayout";
import {
  extractPlatformAccessBlock,
  getPublicPlatformAccessStatus,
} from "../services/platformAccessApi";
import { storeAuthenticatedUser } from "../utils/authSession";
import "../styles/public-experience.css";

const DEFAULT_ISO_CODE = "DE";
const FRONTDOOR_STORAGE_KEY = "e4e_applicant_frontdoor_key";

function getSafeApplicantNextPath(value) {
  const normalized = String(value || "").trim();
  return /^\/dashboard\/applicant(?:[/?].*)?$/u.test(normalized) ? normalized : "";
}

function getSafeReturnToPath(value) {
  const normalized = String(value || "").trim();
  if (!normalized.startsWith("/")) return "";
  const [pathname] = normalized.split("?");
  if (pathname === "/stb/job-marketplace") return normalized;
  if (pathname === "/applicant/frontdoor") return normalized;
  if (/^\/dashboard\/applicant(?:\/.*)?$/u.test(pathname)) return normalized;
  return "";
}

function buildApplicantIntentTarget({ next, returnTo, intent, jobId }) {
  const applicantNextPath = getSafeApplicantNextPath(next);
  const safeReturnTo = getSafeReturnToPath(returnTo);
  if (safeReturnTo && intent === "apply" && String(jobId || "").trim()) {
    const params = new URLSearchParams();
    params.set("intent", "apply");
    params.set("jobId", String(jobId).trim());
    return `${safeReturnTo}${safeReturnTo.includes("?") ? "&" : "?"}${params.toString()}`;
  }
  if (safeReturnTo) return safeReturnTo;
  return applicantNextPath || "/dashboard/applicant";
}

function buildLoginLink(searchParams, fallbackRole = "") {
  const params = new URLSearchParams();
  const frontdoor = String(searchParams.get("frontdoor") || "").trim();
  const next = getSafeApplicantNextPath(searchParams.get("next"));
  const returnTo = getSafeReturnToPath(searchParams.get("returnTo"));
  const intent = String(searchParams.get("intent") || "").trim();
  const jobId = String(searchParams.get("jobId") || "").trim();
  const role = String(searchParams.get("role") || fallbackRole || "").trim().toLowerCase();

  if (frontdoor) params.set("frontdoor", frontdoor);
  if (next) params.set("next", next);
  if (returnTo) params.set("returnTo", returnTo);
  if (intent === "apply" && jobId) {
    params.set("intent", "apply");
    params.set("jobId", jobId);
  }
  if (role === "applicant" || role === "employer") params.set("role", role);

  const query = params.toString();
  return query ? `/login?${query}` : "/login";
}

const roleCards = [
  {
    role: "applicant",
    eyebrow: "Bewerber",
    title: "Ich bin Bewerber",
    description: "Profil aufbauen, Qualifikationen erfassen, Dokumente vorbereiten und passende Jobs finden.",
    cta: "Als Bewerber fortfahren",
  },
  {
    role: "employer",
    eyebrow: "Arbeitgeber",
    title: "Ich bin Arbeitgeber",
    description: "Stellen anlegen, Bewerbungen strukturiert sichten und nächste Schritte vorbereitet steuern.",
    cta: "Als Arbeitgeber fortfahren",
  },
];

const onboardingBenefits = [
  {
    label: "Schritt 1",
    title: "Rolle bewusst wählen",
    text: "Der Einstieg bleibt klar getrennt für Bewerber und Arbeitgeber, damit Recruiting- und Vorbereitungswege sauber starten.",
  },
  {
    label: "Schritt 2",
    title: "Schlanken Zugang anlegen",
    text: "Konto zuerst, weitere Angaben danach. So bleiben Jobs, Qualifikationen, Tests und Dokumente geordnet anschließbar.",
  },
  {
    label: "Einstieg",
    title: "Onboarding statt Endlosformular",
    text: "Die Registrierung führt direkt in den passenden Bereich und vermeidet eine überladene Vollaufnahme ohne klare Produktlogik.",
  },
];

const roleCopy = {
  applicant: {
    eyebrow: "Bewerberkonto",
    title: "Bewerberkonto erstellen",
    description:
      "Starten Sie mit einem schlanken Zugang. Profil, Qualifikationen, Wissen, Dokumente und weitere Angaben können Sie anschließend im Bewerberbereich vervollständigen.",
    submitLabel: "Bewerberkonto erstellen",
    helperText: "Weitere Angaben zu Profil, Qualifikationen und Dokumenten können später ergänzt werden.",
    selectedRoleLabel: "Sie registrieren sich als Bewerber",
    fields: [
      { id: "firstName", label: "Vorname", placeholder: "Vorname", type: "text", autoComplete: "given-name" },
      { id: "lastName", label: "Nachname", placeholder: "Nachname", type: "text", autoComplete: "family-name" },
      { id: "email", label: "E-Mail", placeholder: "name@beispiel.de", type: "email", autoComplete: "email" },
    ],
  },
  employer: {
    eyebrow: "Arbeitgeberkonto",
    title: "Arbeitgeberkonto erstellen",
    description:
      "Starten Sie mit einem schlanken Zugang. Unternehmensdaten, Stellen, Matching-Sicht und weitere Angaben können Sie anschließend im Arbeitgeberbereich vervollständigen.",
    submitLabel: "Arbeitgeberkonto erstellen",
    helperText: "Weitere Angaben zu Unternehmen, Stellen und Bewerbungsprozessen können später ergänzt werden.",
    selectedRoleLabel: "Sie registrieren sich als Arbeitgeber",
    fields: [
      { id: "contactName", label: "Ansprechpartner", placeholder: "Vor- und Nachname", type: "text", autoComplete: "name" },
      { id: "companyName", label: "Unternehmen", placeholder: "Unternehmensname", type: "text", autoComplete: "organization" },
      { id: "email", label: "E-Mail", placeholder: "kontakt@unternehmen.de", type: "email", autoComplete: "email" },
    ],
  },
};

const initialFormState = {
  firstName: "",
  lastName: "",
  contactName: "",
  companyName: "",
  email: "",
  password: "",
  confirmPassword: "",
  acceptedTerms: false,
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function mapRegistrationError(error) {
  const status = Number(error?.response?.status || 0);
  const serverMessage = String(error?.response?.data?.error || "").toLowerCase();

  if (status === 400 || serverMessage.includes("missing required fields")) {
    return "Bitte füllen Sie alle Pflichtfelder aus.";
  }

  return "Die Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.";
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState("");
  const [formState, setFormState] = useState(initialFormState);
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [platformPrecheck, setPlatformPrecheck] = useState(null);
  const [platformAccessNotice, setPlatformAccessNotice] = useState(null);

  const activeRoleCopy = useMemo(() => roleCopy[selectedRole] || null, [selectedRole]);

  useEffect(() => {
    document.title = "Experts4Europe | Registrierung";
  }, []);

  useEffect(() => {
    const requestedRole = String(searchParams.get("role") || "").trim().toLowerCase();
    if (requestedRole === "applicant" || requestedRole === "employer") {
      setSelectedRole(requestedRole);
    }

    const frontdoorKey = String(searchParams.get("frontdoor") || "").trim();
    if (frontdoorKey) {
      localStorage.setItem(FRONTDOOR_STORAGE_KEY, frontdoorKey);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedRole) {
      setPlatformPrecheck(null);
      return undefined;
    }
    let active = true;
    getPublicPlatformAccessStatus({ action: "register", role: selectedRole })
      .then((decision) => {
        if (!active) return;
        setPlatformPrecheck(decision?.allowed ? null : decision);
      })
      .catch(() => {
        if (active) setPlatformPrecheck(null);
      });
    return () => {
      active = false;
    };
  }, [selectedRole]);

  const updateField = (field, value) => {
    setErrorMsg("");
    setPlatformAccessNotice(null);
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const switchRole = (role) => {
    setSelectedRole(role);
    setErrorMsg("");
    setPlatformAccessNotice(null);
  };

  const validateForm = () => {
    if (!selectedRole) {
      return "Bitte wählen Sie zuerst eine Rolle aus.";
    }

    const requiredFields =
      selectedRole === "applicant"
        ? ["firstName", "lastName", "email", "password", "confirmPassword"]
        : ["contactName", "companyName", "email", "password", "confirmPassword"];

    if (requiredFields.some((field) => !String(formState[field] || "").trim())) {
      return "Bitte füllen Sie alle Pflichtfelder aus.";
    }

    if (!isValidEmail(formState.email)) {
      return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }

    if (formState.password !== formState.confirmPassword) {
      return "Die Passwörter stimmen nicht überein.";
    }

    if (!formState.acceptedTerms) {
      return "Bitte bestätigen Sie die Datenschutzbestimmungen und Nutzungsbedingungen.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (platformPrecheck) {
      setPlatformAccessNotice(platformPrecheck);
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");
    setPlatformAccessNotice(null);

    try {
      const payload = {
        email: String(formState.email || "").trim(),
        password: formState.password,
        role: selectedRole,
        iso_code: DEFAULT_ISO_CODE,
      };

      const response = await axios.post(`${config.API_BASE_URL}/api/register`, payload);
      const { user } = response.data;
      const normalizedRole = normalizeRole(user.role);

      storeAuthenticatedUser({
        ...user,
        role: normalizedRole,
      });

      const frontdoorKey =
        String(searchParams.get("frontdoor") || "").trim() || localStorage.getItem(FRONTDOOR_STORAGE_KEY) || "";
      const applicantNextPath = getSafeApplicantNextPath(searchParams.get("next"));

      if (normalizedRole === "applicant" && frontdoorKey) {
        try {
          await axios.post(
            `${config.API_BASE_URL}/api/applicant/frontdoor/intakes/attach`,
            { external_key: frontdoorKey }
          );
        } catch (_err) {
          // Frontdoor-Attach bleibt best effort und blockiert die Registrierung nicht.
        }
      }

      if (normalizedRole === "applicant") {
        navigate(buildApplicantIntentTarget({
          next: applicantNextPath,
          returnTo: searchParams.get("returnTo"),
          intent: searchParams.get("intent"),
          jobId: searchParams.get("jobId"),
        }));
      } else if (normalizedRole === "employer") {
        navigate("/dashboard/employer");
      } else if (normalizedRole === "press") {
        navigate("/dashboard/press");
      } else {
        navigate("/");
      }
    } catch (error) {
      const block = extractPlatformAccessBlock(error);
      if (block) {
        setPlatformAccessNotice(block);
        setErrorMsg("");
        return;
      }
      setErrorMsg(mapRegistrationError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const rightPane = (
    <CCard className="public-core-auth-card public-core-auth-card--wide">
      <CCardBody>
        <div className="public-core-step-indicator">
          <div className={`public-core-step-indicator__item ${selectedRole ? "" : "is-active"}`.trim()}>
            <strong>1. Rolle wählen</strong>
            <span>Bewerber oder Arbeitgeber als klaren Einstieg festlegen.</span>
          </div>
          <div className={`public-core-step-indicator__item ${selectedRole ? "is-active" : ""}`.trim()}>
            <strong>2. Konto anlegen</strong>
            <span>Schlanke Registrierung mit wenigen Pflichtfeldern und späterer Vervollständigung.</span>
          </div>
        </div>

        {!selectedRole ? (
          <>
            <div className="public-core-auth-card__head">
              <span className="public-core-eyebrow">Experts4Europe Registrierung</span>
              <h2>Konto erstellen und den passenden Bereich wählen</h2>
              <p>
                Wählen Sie zuerst, in welchem Bereich Sie Experts4Europe nutzen möchten. Weitere Angaben können
                später ergänzt werden.
              </p>
            </div>

            <CRow className="g-3">
              {roleCards.map((item) => (
                <CCol md={6} key={item.role}>
                  <CCard
                    className={`public-core-role-card ${selectedRole === item.role ? "is-active" : ""}`.trim()}
                    role="button"
                    tabIndex={0}
                    onClick={() => switchRole(item.role)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        switchRole(item.role);
                      }
                    }}
                  >
                    <CCardBody>
                      <span className="public-core-role-card__eyebrow">{item.eyebrow}</span>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <span className="public-core-role-card__cta">{item.cta}</span>
                    </CCardBody>
                  </CCard>
                </CCol>
              ))}
            </CRow>

            <div className="public-core-card-note">
              Die Rollenauswahl bestimmt Ihren Einstieg. Weitere Profildaten können später vervollständigt werden.
              <br />
              Noch unsicher? <Link to="/applicant/frontdoor?entry=unsure">Zuerst Quick Check</Link>
              <br />
              Bereits registriert? <Link to={buildLoginLink(searchParams, "applicant")}>Anmelden</Link>
            </div>
          </>
        ) : (
          <>
            <div className="public-core-auth-card__head">
              <span className="public-core-eyebrow">{activeRoleCopy.eyebrow}</span>
              <h2>{activeRoleCopy.title}</h2>
              <p>{activeRoleCopy.description}</p>
            </div>

            <div className="public-core-role-context">
              <strong>{activeRoleCopy.selectedRoleLabel}</strong>
              <p>
                Startkontext des Kontos bleibt Deutschland. Weitere Angaben können Sie später im passenden Bereich
                ergänzen.
              </p>
              {selectedRole === "applicant" ? (
                <p className="public-core-helper-text">
                  Erst orientieren? <Link to="/applicant/frontdoor?entry=unsure">Applicant Quick Check öffnen</Link>
                </p>
              ) : null}
            </div>

            <CForm className="public-core-form" onSubmit={handleSubmit}>
              {platformPrecheck || platformAccessNotice ? (
                <CCallout color="warning" className="public-core-alert">
                  <strong>Registrierung derzeit eingeschränkt</strong>
                  <p className="mb-1 mt-2">{(platformPrecheck || platformAccessNotice).message}</p>
                  {(platformPrecheck || platformAccessNotice).startsAt || (platformPrecheck || platformAccessNotice).endsAt ? (
                    <p className="mb-0 small">
                      Zeitfenster: {(platformPrecheck || platformAccessNotice).startsAt ? new Date((platformPrecheck || platformAccessNotice).startsAt).toLocaleString("de-DE") : "sofort"}
                      {" - "}
                      {(platformPrecheck || platformAccessNotice).endsAt ? new Date((platformPrecheck || platformAccessNotice).endsAt).toLocaleString("de-DE") : "offen"}
                      {" · "}
                      {(platformPrecheck || platformAccessNotice).timezone || "Europe/Berlin"}
                    </p>
                  ) : null}
                </CCallout>
              ) : null}

              <CRow className="g-3">
                {activeRoleCopy.fields.slice(0, 2).map((field) => (
                  <CCol md={6} key={field.id}>
                    <div className="public-core-form-row">
                      <CFormLabel htmlFor={field.id}>{field.label}</CFormLabel>
                      <CFormInput
                        id={field.id}
                        type={field.type}
                        placeholder={field.placeholder}
                        autoComplete={field.autoComplete}
                        value={formState[field.id]}
                        disabled={isSubmitting || Boolean(platformPrecheck)}
                        onChange={(event) => updateField(field.id, event.target.value)}
                        className="public-core-input"
                      />
                    </div>
                  </CCol>
                ))}
              </CRow>

              <div className="public-core-form-row">
                <CFormLabel htmlFor="register-email">E-Mail</CFormLabel>
                <CFormInput
                  id="register-email"
                  type="email"
                  placeholder={activeRoleCopy.fields[2].placeholder}
                  autoComplete="email"
                  value={formState.email}
                  disabled={isSubmitting || Boolean(platformPrecheck)}
                  onChange={(event) => updateField("email", event.target.value)}
                  className="public-core-input"
                />
              </div>

              <CRow className="g-3">
                <CCol md={6}>
                  <div className="public-core-form-row">
                    <CFormLabel htmlFor="register-password">Passwort</CFormLabel>
                    <CPasswordInput
                      id="register-password"
                      placeholder="Passwort erstellen"
                      autoComplete="new-password"
                      value={formState.password}
                      disabled={isSubmitting || Boolean(platformPrecheck)}
                      onChange={(event) => updateField("password", event.target.value)}
                      className="public-core-input"
                    />
                  </div>
                </CCol>
                <CCol md={6}>
                  <div className="public-core-form-row">
                    <CFormLabel htmlFor="register-confirm-password">Passwort bestätigen</CFormLabel>
                    <CPasswordInput
                      id="register-confirm-password"
                      placeholder="Passwort wiederholen"
                      autoComplete="new-password"
                      value={formState.confirmPassword}
                      disabled={isSubmitting || Boolean(platformPrecheck)}
                      onChange={(event) => updateField("confirmPassword", event.target.value)}
                      className="public-core-input"
                    />
                  </div>
                </CCol>
              </CRow>

              <div className="public-core-checkbox">
                <CFormCheck
                  id="register-terms"
                  checked={formState.acceptedTerms}
                  disabled={isSubmitting || Boolean(platformPrecheck)}
                  onChange={(event) => updateField("acceptedTerms", event.target.checked)}
                  label={
                    <>
                      Ich akzeptiere die <Link to="/datenschutz">Datenschutzbestimmungen</Link> und <Link to="/agb">Nutzungsbedingungen</Link>.
                    </>
                  }
                />
              </div>

              <p className="public-core-helper-text">{activeRoleCopy.helperText}</p>

              {errorMsg ? (
                <CAlert color="danger" className="public-core-alert">
                  {errorMsg}
                </CAlert>
              ) : null}

              <div className="public-core-form-actions">
                <CLoadingButton
                  type="submit"
                  className="public-core-btn public-core-btn--primary public-core-btn--block"
                  disabledOnLoading
                  disabled={Boolean(platformPrecheck)}
                  loading={isSubmitting}
                >
                  {isSubmitting ? "Registrierung läuft..." : (platformPrecheck ? "Registrierung derzeit gesperrt" : activeRoleCopy.submitLabel)}
                </CLoadingButton>
                <CButton
                  type="button"
                  className="public-core-btn public-core-btn--light public-core-btn--block"
                  disabled={isSubmitting}
                  onClick={() => setSelectedRole("")}
                >
                  Rolle wechseln
                </CButton>
              </div>
            </CForm>

            <div className="public-core-card-note">
              Bereits registriert? <Link to={buildLoginLink(searchParams, selectedRole)}>Anmelden</Link>
            </div>
          </>
        )}
      </CCardBody>
    </CCard>
  );

  return (
    <PublicAuthLayout
      activeAuthPath="/register"
      eyebrow="Experts4Europe Registrierung"
      title="Konto erstellen und den passenden Bereich wählen"
      description="Wählen Sie zuerst, in welchem Bereich Sie Experts4Europe für Jobs, Qualifikationen, Tests, Matching und vorbereitete nächste Schritte nutzen möchten. Weitere Angaben können später ergänzt werden."
      benefits={onboardingBenefits}
      rightPane={rightPane}
    />
  );
}
